<template>
  <v-row class="portal-section">
    <v-col cols="12">
      <v-card>
        <v-card-title class="py-1" :style="bannerStyle">
          <v-row align="center">
            <v-col cols="12" class="text-right py-0">
              <v-btn
                icon
                small
                :dark="isDark"
                class="draggable mr-2"
                title="Click and drag to re-order"
              >
                <v-icon small>fa-grip-lines</v-icon>
              </v-btn>
              <v-menu
                :close-on-content-click="false"
                origin="center center"
                transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small :dark="isDark" class icon v-bind="attrs" v-on="on">
                    <v-icon small>fa-edit</v-icon>
                  </v-btn>
                </template>
                <v-card class="grey lighten-4">
                  <v-card-text class="py-2">
                    <v-text-field
                      label="Title"
                      @change="onChange"
                      hide-details
                      v-model="value.title"
                    ></v-text-field>

                    <v-row align="center">
                      <v-col cols="9">
                        <v-text-field
                          label="Banner color"
                          hide-details
                          v-model="value.banner_color"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="3" class="text-center">
                        <v-menu top origin="center center" transition="scale-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              :color="value.banner_color"
                              icon
                              x-large
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon x-large>fas fa-square</v-icon>
                            </v-btn>
                          </template>

                          <v-color-picker
                            class="grey lighten-4"
                            hide-canvas
                            v-model="value.banner_color"
                            @update:color="onBannerColor"
                            show-swatches
                          ></v-color-picker>
                        </v-menu>
                      </v-col>
                    </v-row>

                    <v-row align="center">
                      <v-col cols="9">
                        <v-text-field
                          label="Button color"
                          hide-details
                          v-model="value.button_color"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="3" class="text-center">
                        <v-menu top origin="center center" transition="scale-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              small
                              :color="value.button_color"
                              fab
                              :dark="isButtonDark"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>fas fa-plus</v-icon>
                            </v-btn>
                          </template>

                          <v-color-picker
                            class="grey lighten-4"
                            hide-canvas
                            v-model="value.button_color"
                            @update:color="onButtonColor"
                            show-swatches
                          ></v-color-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-menu>

              <v-btn small icon :dark="isDark" @click="$emit('remove')">
                <v-icon small>fa-times</v-icon>
              </v-btn>
            </v-col>

            <v-col cols="12" class="text-center py-0">
              <h5
                style="word-break: break-word;"
                :class="`${textStyle}`"
                contenteditable="true"
                ref="section-title"
                @blur="updatetitle"
                @keypress.enter="$event.target.blur"
              >{{value.title}}</h5>
            </v-col>
          </v-row>
        </v-card-title>

        <v-tabs
          fixed-tabs
          :show-arrows="false"
          :slider-size="4"
          :dark="isDark"
          :color="isDark ? '' : 'secondary' "
          :slider-color="isDark ? 'yellow' : 'grey darken-2'"
          v-model="tab"
        >
          <v-tab href="#tab-videos" class="tab-title">
            <v-icon small>fa-play</v-icon>
            <span class="ml-2 d-none d-md-block" v-if="value.videos.multiple">Videos</span>
            <span class="ml-2 d-none d-md-block" v-else>Video</span>
            <v-switch
              class="ml-2"
              dense
              :color="value.button_color"
              v-model="value.videos.required"
              @change="onChange"
              :true-value="true"
              :false-value="false"
            ></v-switch>
          </v-tab>
          <v-tab href="#tab-photos" class="tab-title">
            <v-icon small>fa-image</v-icon>
            <span class="ml-2 d-none d-md-block" v-if="value.photos.multiple">Photos</span>
            <span class="ml-2 d-none d-md-block" v-else>Photo</span>
            <v-switch
              class="ml-2"
              dense
              :color="value.button_color"
              v-model="value.photos.required"
              @change="onChange"
              :true-value="true"
              :false-value="false"
            ></v-switch>
          </v-tab>
          <v-tab href="#tab-documents" class="tab-title">
            <v-icon small>fa-file-alt</v-icon>
            <span class="ml-2 d-none d-md-block" v-if="value.photos.multiple">Documents</span>
            <span class="ml-2 d-none d-md-block" v-else>Document</span>
            <v-switch
              class="ml-2"
              dense
              :color="value.button_color"
              v-model="value.documents.required"
              @change="onChange"
              :true-value="true"
              :false-value="false"
            ></v-switch>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item value="tab-videos">
            <v-card flat>
              <v-overlay :absolute="true" :opacity="0.8" :value="!value.videos.required">
                <v-icon>fa-eye-slash</v-icon>
              </v-overlay>
              <v-card-text>
                <v-textarea
                  dense
                  hide-details
                  :rows="3"
                  label="Description"
                  v-model="value.videos.description"
                  @change="onChange"
                ></v-textarea>
                <v-checkbox
                  dense
                  hide-details
                  class="mt-2"
                  :label="`Allow multiple files (max ${$store.state.website.portal_limits.videos})`"
                  @change="onChange"
                  v-model="value.videos.multiple"
                  :true-value="true"
                  :false-value="false"
                ></v-checkbox>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item value="tab-photos">
            <v-card flat>
              <v-overlay :absolute="true" :opacity="0.8" :value="!value.photos.required">
                <v-icon>fa-eye-slash</v-icon>
              </v-overlay>
              <v-card-text>
                <v-textarea
                  dense
                  hide-details
                  :rows="3"
                  label="Description"
                  v-model="value.photos.description"
                  @change="onChange"
                ></v-textarea>
                <v-checkbox
                  dense
                  hide-details
                  class="mt-2"
                  :label="`Allow multiple files (max ${$store.state.website.portal_limits.photos})`"
                  v-model="value.photos.multiple"
                  @change="onChange"
                  :true-value="true"
                  :false-value="false"
                ></v-checkbox>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item value="tab-documents">
            <v-card flat>
              <v-overlay :absolute="true" :opacity="0.8" :value="!value.documents.required">
                <v-icon>fa-eye-slash</v-icon>
              </v-overlay>
              <v-card-text>
                <v-textarea
                  dense
                  hide-details
                  label="Description"
                  :rows="3"
                  v-model="value.documents.description"
                ></v-textarea>
                <v-checkbox
                  dense
                  hide-details
                  class="mt-2"
                  :label="`Allow multiple files (max ${$store.state.website.portal_limits.documents})`"
                  v-model="value.documents.multiple"
                  :true-value="true"
                  :false-value="false"
                ></v-checkbox>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  props: ["value"],
  name: "PortalSection",
  data() {
    return {
      tab: ""
    };
  },
  created() {
    setTimeout(
      function() {
        this.setTab();
      }.bind(this),
      100
    );
  },
  computed: {
    isDark() {
      return this.$helpers.lightOrDark(this.value.banner_color) === "dark";
    },
    isButtonDark() {
      return this.$helpers.lightOrDark(this.value.button_color) === "dark";
    },
    textStyle() {
      return this.isDark ? "white--text" : "secondary--text";
    },
    bannerStyle() {
      return `background: ${this.value.banner_color}`;
    }
  },
  methods: {
    updatetitle() {
      if (this.$refs["section-title"].innerText !== this.value.title) {
        this.value.title = this.$refs["section-title"].innerText;
        this.onChange();
      }
    },
    setTab() {
      if (this.value.videos.required) {
        this.tab = "tab-videos";
      } else if (this.value.photos.required) {
        this.tab = "tab-photos";
      } else if (this.value.documents.required) {
        this.tab = "tab-documents";
      }
    },
    onBannerColor(to) {
      this.$emit("bannerColor", to.hex);
      this.onChange();
    },
    onButtonColor(to) {
      this.$emit("buttonColor", to.hex);
      this.onChange();
    },
    onChange() {
      this.$emit("input", this.value);
    }
  }
};
</script>

<style>
.tab-title {
  font-size: 10px;
}
</style>